import { useEffect, useMemo, useState, createContext, useContext } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import DietaryMark from "@/components/DietaryMark";
import { Plus, Trash2, Save, Eye, EyeOff, X, GripVertical, Copy } from "lucide-react";
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Party items may still be strings on very old records — normalise into { name, dietary } with a stable client key
const normItem = (it) => {
  const base = typeof it === "string" ? { name: it, dietary: "" } : { name: it?.name || "", dietary: it?.dietary || "" };
  return { _key: it?._key || crypto.randomUUID(), ...base };
};

const withSecKey = (s) => ({ _key: s._key || crypto.randomUUID(), name: s.name, items: (s.items || []).map(normItem) });

const emptyPkg = { price: 799, tagline: "New Package", sections: [{ _key: "seed-starters", name: "Starters", items: [{ _key: "seed-item-1", name: "", dietary: "veg" }] }], active: true, order: 99 };

const SortableCtx = createContext({ attributes: {}, listeners: {} });

export default function StaffPartyMenu() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({});
  const [newPkg, setNewPkg] = useState(emptyPkg);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const load = async () => {
    try {
      const { data } = await api.get("/staff/party-packages");
      // Normalise sections' items into { name, dietary } for consistent editing
      const normalised = data.map((pkg) => ({
        ...pkg,
        sections: (pkg.sections || []).map(withSecKey),
      }));
      setItems(normalised);
      const d = {};
      normalised.forEach((p) => { d[p.id] = JSON.parse(JSON.stringify(p)); });
      setDraft(d);
    } catch { toast.error("Could not load packages."); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const updateField = (id, key, val) => setDraft((prev) => ({ ...prev, [id]: { ...prev[id], [key]: val } }));

  const updateSectionName = (id, sIdx, val) => setDraft((prev) => {
    const copy = { ...prev, [id]: { ...prev[id], sections: prev[id].sections.map((s, i) => i === sIdx ? { ...s, name: val } : s) } };
    return copy;
  });

  const updateSectionItem = (id, sIdx, iIdx, key, val) => setDraft((prev) => ({
    ...prev,
    [id]: {
      ...prev[id],
      sections: prev[id].sections.map((s, i) =>
        i === sIdx ? { ...s, items: s.items.map((it, j) => j === iIdx ? { ...normItem(it), [key]: val } : it) } : s
      ),
    },
  }));

  const addItem = (id, sIdx) => setDraft((prev) => ({
    ...prev,
    [id]: {
      ...prev[id],
      sections: prev[id].sections.map((s, i) =>
        i === sIdx ? { ...s, items: [...s.items, { _key: crypto.randomUUID(), name: "", dietary: "veg" }] } : s
      ),
    },
  }));

  const removeItem = (id, sIdx, iIdx) => setDraft((prev) => ({
    ...prev,
    [id]: {
      ...prev[id],
      sections: prev[id].sections.map((s, i) =>
        i === sIdx ? { ...s, items: s.items.filter((_, j) => j !== iIdx) } : s
      ),
    },
  }));

  const addSection = (id) => setDraft((prev) => ({
    ...prev,
    [id]: { ...prev[id], sections: [...prev[id].sections, { _key: crypto.randomUUID(), name: "New Section", items: [{ _key: crypto.randomUUID(), name: "", dietary: "veg" }] }] },
  }));

  const removeSection = (id, sIdx) => setDraft((prev) => ({
    ...prev,
    [id]: { ...prev[id], sections: prev[id].sections.filter((_, i) => i !== sIdx) },
  }));

  const duplicateSection = (id, sIdx) => setDraft((prev) => {
    const src = prev[id].sections[sIdx];
    if (!src) return prev;
    const clone = {
      _key: crypto.randomUUID(),
      name: `${src.name} (copy)`,
      items: (src.items || []).map((it) => ({ _key: crypto.randomUUID(), name: it.name || "", dietary: it.dietary || "" })),
    };
    const next = [...prev[id].sections];
    next.splice(sIdx + 1, 0, clone);
    return { ...prev, [id]: { ...prev[id], sections: next } };
  });

  const reorderItems = (id, sIdx, fromKey, toKey) => setDraft((prev) => ({
    ...prev,
    [id]: {
      ...prev[id],
      sections: prev[id].sections.map((s, i) => {
        if (i !== sIdx) return s;
        const from = s.items.findIndex((x) => x._key === fromKey);
        const to = s.items.findIndex((x) => x._key === toKey);
        if (from < 0 || to < 0 || from === to) return s;
        return { ...s, items: arrayMove(s.items, from, to) };
      }),
    },
  }));

  const reorderSections = (id, fromKey, toKey) => setDraft((prev) => {
    const from = prev[id].sections.findIndex((x) => x._key === fromKey);
    const to = prev[id].sections.findIndex((x) => x._key === toKey);
    if (from < 0 || to < 0 || from === to) return prev;
    return { ...prev, [id]: { ...prev[id], sections: arrayMove(prev[id].sections, from, to) } };
  });

  const save = async (id) => {
    try {
      const d = draft[id];
      const cleaned = {
        ...d,
        price: Number(d.price),
        order: Number(d.order),
        sections: d.sections
          .map((s) => ({
            name: s.name.trim(),
            items: s.items
              .map((it) => ({ name: (normItem(it).name || "").trim(), dietary: normItem(it).dietary || "" }))
              .filter((it) => it.name),
          }))
          .filter((s) => s.name && s.items.length > 0),
      };
      const { data } = await api.patch(`/staff/party-packages/${id}`, cleaned);
      // Re-normalise the response for editing
      const norm = { ...data, sections: (data.sections || []).map((s) => ({ name: s.name, items: (s.items || []).map(normItem) })) };
      setItems((prev) => prev.map((p) => (p.id === id ? norm : p)));
      setDraft((prev) => ({ ...prev, [id]: JSON.parse(JSON.stringify(norm)) }));
      toast.success("Saved");
    } catch { toast.error("Save failed"); }
  };

  const toggle = async (pkg) => {
    try {
      const { data } = await api.patch(`/staff/party-packages/${pkg.id}`, { active: !pkg.active });
      setItems((prev) => prev.map((p) => (p.id === pkg.id ? data : p)));
      toast.success(data.active ? "Visible on site" : "Hidden");
    } catch { toast.error("Toggle failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this package?")) return;
    try {
      await api.delete(`/staff/party-packages/${id}`);
      setItems((prev) => prev.filter((p) => p.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      const cleaned = {
        ...newPkg,
        price: Number(newPkg.price),
        order: Number(newPkg.order),
        sections: newPkg.sections
          .map((s) => ({
            name: s.name.trim(),
            items: s.items
              .map((it) => ({ name: (normItem(it).name || "").trim(), dietary: normItem(it).dietary || "" }))
              .filter((it) => it.name),
          }))
          .filter((s) => s.name && s.items.length > 0),
      };
      if (cleaned.sections.length === 0) {
        toast.error("Add at least one section with an item.");
        return;
      }
      await api.post("/staff/party-packages", cleaned);
      toast.success("Package added");
      setNewPkg(emptyPkg);
      setCreating(false);
      load();
    } catch { toast.error("Create failed"); }
  };

  return (
    <div data-testid="staff-party-menu">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Party Menu</div>
          <h1 className="mt-2 text-4xl">Package editor</h1>
          <p className="mt-3 text-[hsl(var(--ink-soft))] max-w-xl">
            These are the party packages shown on the public Menu page and used by the Kitchen Brief.
            Edit prices, add sections, add or remove dishes.
          </p>
        </div>
        <button data-testid="party-new-btn" onClick={() => setCreating((v) => !v)} className="btn-brass !py-2.5 !px-5 text-sm">
          <Plus size={14} /> {creating ? "Cancel" : "New package"}
        </button>
      </div>

      {creating && (
        <NewPackageForm
          value={newPkg}
          onChange={setNewPkg}
          onSubmit={create}
        />
      )}

      {loading && <div className="mt-8 text-[hsl(var(--ink-soft))]">Loading...</div>}

      <div className="mt-8 space-y-6">
        {items.map((pkg) => {
          const d = draft[pkg.id];
          if (!d) return null;
          return (
            <div key={pkg.id} data-testid={`party-row-${pkg.id}`} className="border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] p-6">
              <div className="grid grid-cols-1 sm:grid-cols-6 gap-3 items-end">
                <label className="text-xs sm:col-span-1">
                  <span className="uppercase tracking-wider text-[hsl(var(--ink-soft))]">Price ₹</span>
                  <input type="number" className="mt-1 tbb-inp w-full" value={d.price} onChange={(e) => updateField(pkg.id, "price", e.target.value)} />
                </label>
                <label className="text-xs sm:col-span-3">
                  <span className="uppercase tracking-wider text-[hsl(var(--ink-soft))]">Tagline</span>
                  <input className="mt-1 tbb-inp w-full" value={d.tagline} onChange={(e) => updateField(pkg.id, "tagline", e.target.value)} />
                </label>
                <label className="text-xs">
                  <span className="uppercase tracking-wider text-[hsl(var(--ink-soft))]">Order</span>
                  <input type="number" className="mt-1 tbb-inp w-full" value={d.order} onChange={(e) => updateField(pkg.id, "order", e.target.value)} />
                </label>
                <div className="flex gap-2 justify-end">
                  <button data-testid={`party-save-${pkg.id}`} onClick={() => save(pkg.id)} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 rounded bg-[hsl(var(--forest))] text-[hsl(var(--bg))]">
                    <Save size={12} /> Save
                  </button>
                  <button data-testid={`party-toggle-${pkg.id}`} onClick={() => toggle(pkg)} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 rounded border border-[hsl(var(--rule))]">
                    {pkg.active ? <><EyeOff size={12} /> Hide</> : <><Eye size={12} /> Show</>}
                  </button>
                  <button data-testid={`party-delete-${pkg.id}`} onClick={() => remove(pkg.id)} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 rounded border border-[hsl(6_62%_45%)] text-[hsl(6_62%_45%)]">
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>

              <div className="rule-brass my-5" />

              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => {
                if (!e.over || e.active.id === e.over.id) return;
                reorderSections(pkg.id, e.active.id, e.over.id);
              }}>
                <SortableContext items={d.sections.map((s) => s._key)} strategy={verticalListSortingStrategy}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {d.sections.map((s, sIdx) => (
                      <SortableSection key={s._key} id={s._key}>
                        <div className="flex items-center gap-2">
                          <SortableSection.Handle data-testid={`party-sec-handle-${sIdx}`} />
                          <input className="tbb-inp flex-1 text-sm font-semibold" value={s.name} onChange={(e) => updateSectionName(pkg.id, sIdx, e.target.value)} />
                          <button type="button" data-testid={`party-sec-duplicate-${sIdx}`} onClick={() => duplicateSection(pkg.id, sIdx)} className="text-[hsl(var(--brass-2))] hover:text-[hsl(var(--forest))]" aria-label="Duplicate section" title="Duplicate section">
                            <Copy size={13} />
                          </button>
                          <button type="button" onClick={() => removeSection(pkg.id, sIdx)} className="text-[hsl(6_62%_45%)]" aria-label="Remove section">
                            <X size={14} />
                          </button>
                        </div>
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => {
                          if (!e.over || e.active.id === e.over.id) return;
                          reorderItems(pkg.id, sIdx, e.active.id, e.over.id);
                        }}>
                          <SortableContext items={s.items.map((it) => it._key)} strategy={verticalListSortingStrategy}>
                            <ul className="mt-2 space-y-1">
                              {s.items.map((it, iIdx) => {
                                const item = normItem(it);
                                return (
                                  <SortableItem key={it._key} id={it._key}>
                                    <SortableItem.Handle data-testid={`party-item-handle-${sIdx}-${iIdx}`} />
                                    <DietaryMark value={item.dietary} />
                                    <select
                                      className="tbb-inp text-xs !py-1 !px-1.5"
                                      value={item.dietary}
                                      onChange={(e) => updateSectionItem(pkg.id, sIdx, iIdx, "dietary", e.target.value)}
                                      aria-label="Dietary"
                                    >
                                      <option value="veg">V</option>
                                      <option value="non-veg">NV</option>
                                      <option value="">—</option>
                                    </select>
                                    <input className="tbb-inp flex-1 text-sm" value={item.name} onChange={(e) => updateSectionItem(pkg.id, sIdx, iIdx, "name", e.target.value)} />
                                    <button type="button" onClick={() => removeItem(pkg.id, sIdx, iIdx)} className="text-[hsl(var(--ink-soft))] hover:text-[hsl(6_62%_45%)]" aria-label="Remove item">
                                      <X size={12} />
                                    </button>
                                  </SortableItem>
                                );
                              })}
                            </ul>
                          </SortableContext>
                        </DndContext>
                        <button type="button" onClick={() => addItem(pkg.id, sIdx)} className="mt-2 text-xs text-[hsl(var(--forest))] font-semibold">+ Add item</button>
                      </SortableSection>
                    ))}
                  </div>
                </SortableContext>
              </DndContext>

              <button type="button" onClick={() => addSection(pkg.id)} className="mt-4 text-sm text-[hsl(var(--forest))] font-semibold underline underline-offset-4">
                + Add section
              </button>
            </div>
          );
        })}
      </div>

      <style>{`
        .tbb-inp {
          background: hsl(var(--bg));
          border: 1px solid hsl(var(--rule));
          border-radius: 4px;
          padding: 0.5rem 0.75rem;
          font-family: inherit;
          font-size: 0.85rem;
          color: hsl(var(--ink));
        }
        .tbb-inp:focus { outline: none; border-color: hsl(var(--forest)); }
      `}</style>
    </div>
  );
}

function NewPackageForm({ value, onChange, onSubmit }) {
  const setSectionName = (idx, v) => onChange({ ...value, sections: value.sections.map((s, i) => i === idx ? { ...s, name: v } : s) });
  const setItemField = (sIdx, iIdx, key, v) => onChange({
    ...value,
    sections: value.sections.map((s, i) =>
      i === sIdx ? { ...s, items: s.items.map((it, j) => j === iIdx ? { ...normItem(it), [key]: v } : it) } : s
    ),
  });
  const addItem = (sIdx) => onChange({
    ...value,
    sections: value.sections.map((s, i) => i === sIdx ? { ...s, items: [...s.items, { _key: crypto.randomUUID(), name: "", dietary: "veg" }] } : s),
  });
  const addSection = () => onChange({ ...value, sections: [...value.sections, { _key: crypto.randomUUID(), name: "New Section", items: [{ _key: crypto.randomUUID(), name: "", dietary: "veg" }] }] });

  return (
    <form data-testid="party-new-form" onSubmit={onSubmit} className="mt-6 border border-[hsl(var(--brass))] bg-[hsl(var(--bg-2))] p-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <input type="number" placeholder="Price" data-testid="new-pkg-price" className="tbb-inp" value={value.price} onChange={(e) => onChange({ ...value, price: e.target.value })} />
        <input placeholder="Tagline" data-testid="new-pkg-tagline" className="tbb-inp sm:col-span-2" value={value.tagline} onChange={(e) => onChange({ ...value, tagline: e.target.value })} />
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        {value.sections.map((s, sIdx) => (
          <div key={s._key || `newsec-${sIdx}`} className="border border-[hsl(var(--rule))] p-3 bg-[hsl(var(--bg))]">
            <input className="tbb-inp w-full text-sm font-semibold" value={s.name} onChange={(e) => setSectionName(sIdx, e.target.value)} />
            <ul className="mt-2 space-y-1">
              {s.items.map((it, iIdx) => {
                const item = normItem(it);
                return (
                  <li key={it._key || `newit-${iIdx}`} className="flex items-center gap-1.5">
                    <DietaryMark value={item.dietary} />
                    <select className="tbb-inp text-xs !py-1 !px-1.5" value={item.dietary} onChange={(e) => setItemField(sIdx, iIdx, "dietary", e.target.value)}>
                      <option value="veg">V</option>
                      <option value="non-veg">NV</option>
                      <option value="">—</option>
                    </select>
                    <input className="tbb-inp flex-1 text-sm" value={item.name} onChange={(e) => setItemField(sIdx, iIdx, "name", e.target.value)} />
                  </li>
                );
              })}
            </ul>
            <button type="button" onClick={() => addItem(sIdx)} className="mt-2 text-xs text-[hsl(var(--forest))] font-semibold">+ Add item</button>
          </div>
        ))}
      </div>
      <button type="button" onClick={addSection} className="mt-3 text-sm text-[hsl(var(--forest))] font-semibold">+ Add section</button>
      <div className="mt-6">
        <button type="submit" data-testid="new-pkg-submit" className="btn-brass">Create package</button>
      </div>
      <style>{`
        .tbb-inp {
          background: hsl(var(--bg));
          border: 1px solid hsl(var(--rule));
          border-radius: 4px;
          padding: 0.5rem 0.75rem;
          font-family: inherit;
          font-size: 0.85rem;
          color: hsl(var(--ink));
        }
      `}</style>
    </form>
  );
}


// ---------- Sortable wrappers ----------
function SortableSection({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  return (
    <div ref={setNodeRef} style={style} className="border border-[hsl(var(--rule))] p-3 bg-[hsl(var(--bg))]">
      <SortableCtx.Provider value={{ attributes, listeners }}>
        {children}
      </SortableCtx.Provider>
    </div>
  );
}
SortableSection.Handle = function Handle(props) {
  const { attributes, listeners } = useContext(SortableCtx);
  return (
    <button type="button" {...attributes} {...listeners} className="text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--brass-2))] cursor-grab active:cursor-grabbing touch-none" aria-label="Drag section" {...props}>
      <GripVertical size={14} />
    </button>
  );
};

function SortableItem({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  return (
    <li ref={setNodeRef} style={style} className="flex items-center gap-1.5">
      <SortableCtx.Provider value={{ attributes, listeners }}>
        {children}
      </SortableCtx.Provider>
    </li>
  );
}
SortableItem.Handle = function Handle(props) {
  const { attributes, listeners } = useContext(SortableCtx);
  return (
    <button type="button" {...attributes} {...listeners} className="text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--brass-2))] cursor-grab active:cursor-grabbing touch-none" aria-label="Drag item" {...props}>
      <GripVertical size={12} />
    </button>
  );
};
